import type { OptimizationGroupModel, ScheduleModel } from '@/shared/generated'

import React, { useMemo } from 'react'
import { format } from 'date-fns'
import {
	all,
	any,
	find,
	flatten,
	indexBy,
	map,
	omitAll,
	orderBy,
	pipe,
	prop,
	uniq,
	values,
} from 'lodash/fp'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import {
	formatSchedule,
	useCreateScheduleMutation,
	useSchedulesQuery,
	useUpdateScheduleMutation,
	weekdays,
} from '@/entities/optimization-schedule'
import { useSession } from '@/entities/session'
import { getUsername, useAllUsersQuery } from '@/entities/user'

import { ErrorBoundaryWithUser } from '@/shared/components/ErrorBoundary'
import {
	FormProvider,
	RHFDate,
	RHFMultiSelect,
	RHFSwitch,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Badge } from '@/shared/components/ui/badge'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import {
	Field,
	FieldContent,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from '@/shared/components/ui/field'
import { FormField, FormItem } from '@/shared/components/ui/form'
import { Loader } from '@/shared/components/ui/loader'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import {
	ToggleGroup,
	ToggleGroupItem,
} from '@/shared/components/ui/toggle-group'
import { useAppStore } from '@/shared/contexts/app'
import useStep from '@/shared/hooks/useStep'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'
import { DateFormat } from '@/shared/tools/dates'

type FormData = Omit<ScheduleModel, 'id' | 'nearest_date'>

const canGoNext = (step: number, formData: FormData) => {
	switch (step) {
		case 1: {
			if (!formData) return false
			const checks: (string | boolean)[] = [
				formData.name,
				formData.start_date,
				formData.optimization_groups.length > 0,
			]
			if (formData.repeat) {
				checks.push(!!formData?.repeat_days?.length)
				checks.push(!!formData.repeat_week_interval)
			}

			return all(Boolean, checks)
		}
		case 2:
			return true
		default:
			return false
	}
}
const getDefaultStartDate = () => format(new Date(), DateFormat.system)
export function OptimizationScheduleModal({
	isOpen,
	close,
	id,
	daraftData,
}: {
	isOpen: boolean
	close: () => void
	id?: string
	daraftData?: Pick<ScheduleModel, 'optimization_groups'>
}) {
	const { t } = useTranslation()

	const dateFormat = useAppStore((state) => state.dateFormat)
	const isNew = !id
	const session = useSession()
	const [step, stepsControl] = useStep({ maxStep: 2 })
	const scheduleQuery = useSchedulesQuery<ScheduleModel | undefined>({
		select: find<ScheduleModel>({ id }),
	})
	const groupsQuery = useOptimizationGroupsQuery()
	const { data: scheduledGroupsIds } = useSchedulesQuery<string[]>({
		select: pipe([values, map(prop('optimization_groups')), flatten, uniq]),
	})
	const users = useAllUsersQuery()
	const createSchedule = useCreateScheduleMutation()
	const updateSchedule = useUpdateScheduleMutation()
	const isPending = any(prop('isPending'), [createSchedule, updateSchedule])
	const isLoading = any(prop('isLoading'), [scheduleQuery, groupsQuery])
	const schedule = scheduleQuery.data
	const defaultValues = useMemo(
		() => ({
			start_date: getDefaultStartDate(),
			name: '',
			repeat: false,
			repeat_days: [],
			repeat_week_interval: undefined,
			optimization_groups: daraftData?.optimization_groups || [],
			notify_users: [],
		}),
		[daraftData]
	)
	const methods = useForm<FormData>({
		defaultValues: schedule
			? omitAll(['id', 'nearest_date'], schedule)
			: defaultValues,
	})
	const closeModal = () => close()
	const _onSubmit = async (payload: FormData) => {
		if (isNew) {
			ampli.autorunCreateSchedule({
				'start date': payload.start_date,
				'days of a week': (payload.repeat_days || []).map((d) => weekdays[d]),
				repeat: payload.repeat,
				'amount of ogs': payload.optimization_groups.length,
				'amount of notif users': payload.notify_users?.length,
			})
			createSchedule.mutateAsync(payload).then(() => {
				closeModal()
			})
		} else {
			ampli.autorunUpdateSchedule({
				'configuration name': payload.name,
			})
			updateSchedule.mutateAsync({ ...payload, id: schedule!.id }).then(() => {
				closeModal()
			})
		}
	}
	const groupsMap = indexBy<OptimizationGroupModel>('id', groupsQuery.data)
	const groupsOptions = groupsQuery.data?.map((g) => ({
		value: g.id,
		label: g.name,
	}))
	const usersOptions = orderBy(
		['me', 'label'],
		['desc', 'asc'],
		users.data
			.filter(({ apps }) =>
				any(
					(g) => groupsMap[g],
					find({ name: 'pricing_platform' }, apps)?.entities
						?.optimization_group ?? []
				)
			)
			.map((user) => ({
				value: user.id,
				label: user.email,
				username: getUsername(user),
				me: session.user?.id == user.id,
			}))
	)
	const disabledGroups = schedule
		? scheduledGroupsIds?.filter(
				(id) => !schedule.optimization_groups.includes(id)
			)
		: scheduledGroupsIds
	const selectedGroups = methods.watch('optimization_groups')

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='flex max-h-[90vh] max-w-xl flex-col'>
				<ModalHeader>
					<ModalTitle>{t('schedule_repricing')}</ModalTitle>
				</ModalHeader>
				<ModalBody className='flex flex-col overflow-y-auto p-0'>
					<ErrorBoundaryWithUser showDialog>
						<FormProvider
							className='flex h-full flex-col px-4'
							methods={methods}
							onSubmit={methods.handleSubmit(_onSubmit)}
						>
							<Blanket
								isTinted={isLoading || isPending}
								className='absolute flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-xs dark:bg-black/30'
							>
								<Loader />
							</Blanket>
							{step === 1 && (
								<FieldSet className='flex h-full flex-col gap-6'>
									<RHFTextField
										required
										name='name'
										label={t('schedule_form_name')}
										placeholder='eg. "Weekly optimization"'
									/>
									<RHFMultiSelect
										name='optimization_groups'
										label={t('optimization_groups', {
											defaultValue: 'Optimization groups',
										})}
										options={groupsOptions || []}
										isOptionDisabled={(option) =>
											!!disabledGroups?.includes(option.value)
										}
									/>
									<FieldGroup>
										<RHFDate
											format={dateFormat}
											name='start_date'
											label={t('general_date')}
										/>
										<Field className='grid gap-1.5'>
											<FieldLabel>{t('general_time')}</FieldLabel>
											<FieldContent>
												<Badge variant='info'>
													{t('schedule_after_integration')}
												</Badge>
											</FieldContent>
										</Field>

										<RHFSwitch name='repeat' label={t('general_repeat')} />
									</FieldGroup>
									<FieldGroup
										className={cn('flex flex-row items-center gap-2', {
											'pointer-events-none opacity-50':
												!methods.watch('repeat'),
										})}
									>
										<RHFTextField
											disabled={!methods.watch('repeat')}
											className='w-32'
											type='number'
											name='repeat_week_interval'
											label={t('general_repeat_every')}
											addonAfter={t('general_weeks')}
										/>
										<FormField
											name='repeat_days'
											control={methods.control}
											render={({ field: { value, onChange } }) => (
												<FormItem className='mt-6.5 w-full'>
													<FieldContent className='mt-7'>
														<ToggleGroup
															spacing={2}
															disabled={!methods.watch('repeat')}
															value={value?.map(String)}
															type='multiple'
															onValueChange={(v) => onChange(v.map(Number))}
															className='w-full'
														>
															{weekdays.map((day, i) => (
																<ToggleGroupItem
																	className='uppercase data-[state=off]:text-muted-foreground'
																	value={i.toString()}
																	key={day}
																>
																	{t(day).substr(0, 3)}
																</ToggleGroupItem>
															))}
														</ToggleGroup>
													</FieldContent>
												</FormItem>
											)}
										/>
									</FieldGroup>
									<FieldGroup>
										<RHFMultiSelect
											name='notify_users'
											label={t('notif')}
											description={t('autorun_notif_users_hint')}
											options={usersOptions}
											placeholder={t('select_users')}
										/>
									</FieldGroup>
								</FieldSet>
							)}
							{step === 2 && (
								<div className='grid grid-cols-1 gap-6'>
									<div>
										<h5>
											{t('schedule_confirmation_groups')}(
											{selectedGroups.length}):
										</h5>
										<ul className='list-disc pl-4 text-sm'>
											{selectedGroups.map((id: string) => (
												<li key={id}>{groupsMap[id]?.name || 'None'}</li>
											))}
										</ul>
									</div>
									<div>
										<h5>{t('schedule_confirmation_settings')}</h5>
										<div className='mt-2.5 text-sm'>
											<p className='text-wrap whitespace-break-spaces'>
												{formatSchedule(methods.watch() as ScheduleModel, t)}
											</p>
										</div>
									</div>
								</div>
							)}
						</FormProvider>
					</ErrorBoundaryWithUser>
				</ModalBody>
				{!(isLoading || isPending) && (
					<ModalFooter className='justify-end gap-2'>
						<Button className='mr-auto' onClick={closeModal}>
							{t('general_cancel')}
						</Button>
						{stepsControl.canGoToPrevStep && (
							<Button
								onClick={stepsControl.goToPrevStep}
								disabled={!stepsControl.canGoToPrevStep}
							>
								<ChevronLeftIcon />
								{t('general_back')}
							</Button>
						)}
						{stepsControl.canGoToNextStep && (
							<Button
								variant='primary'
								disabled={!canGoNext(step, methods.watch())}
								onClick={stepsControl.goToNextStep}
							>
								{t('general_next')}
								<ChevronRightIcon />
							</Button>
						)}
						{!stepsControl.canGoToNextStep && (
							<Button
								variant='primary'
								onClick={methods.handleSubmit(_onSubmit)}
							>
								{t('general_apply')}
							</Button>
						)}
					</ModalFooter>
				)}
			</ModalContent>
		</Modal>
	)
}
